import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "@tanstack/react-router";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Calendar } from "@/components/ui/calendar";
import { createBookingFn } from "./BookingDialog.functions";
import { useAuth } from "@/hooks/useAuth";
import { toast } from "sonner";
import { findInsurance, trackListingMetric, INSURANCES } from "@/data/heallexa";
import type { AvailabilityWindow } from "@/components/AvailabilityStrip";
import {
  CalendarIcon, ChevronLeft, ChevronRight, Clock, ShieldCheck,
  FileText, CheckCircle2, ArrowLeft, ArrowRight, Loader2, LogIn, Sparkles,
} from "lucide-react";
import { cn } from "@/lib/utils";

const DAY_KEYS = ["sun", "mon", "tue", "wed", "thu", "fri", "sat"] as const;

function toMinutes(t: string) { const [h, m] = t.split(":").map(Number); return (h || 0) * 60 + (m || 0); }
function pad(n: number) { return String(n).padStart(2, "0"); }
function fromMinutes(mins: number) { return `${pad(Math.floor(mins / 60))}:${pad(mins % 60)}`; }
function format12h(t: string) {
  const [h, m] = t.split(":").map(Number);
  const ampm = h >= 12 ? "PM" : "AM";
  const hr = ((h + 11) % 12) + 1;
  return `${hr}:${pad(m)} ${ampm}`;
}

function slotsForWindow(w: AvailabilityWindow): string[] {
  const start = toMinutes(w.start_time);
  const end = toMinutes(w.end_time);
  const step = Math.max(5, w.slot_duration_minutes || 30);
  const out: string[] = [];
  for (let mm = start; mm + step <= end; mm += step) out.push(fromMinutes(mm));
  return out;
}

const DEFAULT_SLOTS = [
  "09:00", "09:30", "10:00", "10:30", "11:00", "11:30",
  "13:00", "13:30", "14:00", "14:30", "15:00", "15:30", "16:00", "16:30",
];

function buildDaySlots(date: Date, windows: AvailabilityWindow[], availableDays: string[]) {
  const dow = date.getDay();
  if (windows.length > 0) {
    const todays = windows.filter((w) => w.is_active && w.day_of_week === dow);
    const set = new Set<string>();
    for (const w of todays) for (const s of slotsForWindow(w)) set.add(s);
    return Array.from(set).sort();
  }
  const key = DAY_KEYS[dow];
  if (!availableDays?.map((d) => d.toLowerCase()).includes(key)) return [];
  return DEFAULT_SLOTS;
}

const COMMON_REASONS = [
  "New patient consultation",
  "Annual check-up / preventive visit",
  "Follow-up appointment",
  "Discuss test results",
  "New or worsening symptoms",
  "Prescription refill",
  "Second opinion",
  "Referral needed",
  "Other (I'll describe below)",
];

type Step = "datetime" | "insurance" | "reason" | "confirm";

export function BookingDialog({
  open,
  onOpenChange,
  providerId,
  providerName,
  providerInsuranceIds,
  availableDays,
  windows,
  initialDate,
  initialSlot,
}: {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  providerId: string;
  providerName: string;
  providerInsuranceIds: string[];
  availableDays: string[];
  windows: AvailabilityWindow[];
  initialDate?: Date | null;
  initialSlot?: string | null;
}) {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>("datetime");
  const [month, setMonth] = useState<Date>(() => { const d = new Date(); d.setDate(1); return d; });
  const [date, setDate] = useState<Date | undefined>(undefined);
  const [slot, setSlot] = useState<string | null>(null);
  const [paymentMethod, setPaymentMethod] = useState<string>("");
  const [reason, setReason] = useState<string>("");
  const [reasonDetail, setReasonDetail] = useState<string>("");
  const [submitting, setSubmitting] = useState(false);

  const today = useMemo(() => { const d = new Date(); d.setHours(0,0,0,0); return d; }, []);

  // Reset when reopened — seed with preselected date/slot if provided
  useEffect(() => {
    if (!open) return;
    if (initialDate && initialSlot) {
      setDate(initialDate);
      setSlot(initialSlot);
      setMonth(() => { const d = new Date(initialDate); d.setDate(1); return d; });
      setStep("insurance");
    } else {
      setStep("datetime");
      setSlot(null);
    }
    setReasonDetail("");
  }, [open, initialDate, initialSlot]);

  const insuranceOptions = useMemo(() => {
    const list = (providerInsuranceIds ?? [])
      .map((id) => findInsurance(id))
      .filter(Boolean) as Array<{ id: string; short: string; name?: string }>;
    return list;
  }, [providerInsuranceIds]);

  const otherInsuranceOptions = useMemo(() => {
    const accepted = new Set((providerInsuranceIds ?? []));
    return INSURANCES
      .filter((i) => !accepted.has(i.id) && i.id !== "selfpay")
      .sort((a, b) => a.short.localeCompare(b.short));
  }, [providerInsuranceIds]);

  const daySlots = useMemo(
    () => (date ? buildDaySlots(date, windows, availableDays) : []),
    [date, windows, availableDays]
  );

  const isDayDisabled = (d: Date) => {
    if (d < today) return true;
    const dow = d.getDay();
    if (windows.length > 0) {
      return !windows.some((w) => w.is_active && w.day_of_week === dow);
    }
    const key = DAY_KEYS[dow];
    return !(availableDays?.map((x) => x.toLowerCase()).includes(key));
  };

  const dateLabel = date
    ? date.toLocaleDateString(undefined, { weekday: "long", month: "long", day: "numeric" })
    : "";
  const monthLabel = month.toLocaleDateString(undefined, { month: "long", year: "numeric" });

  const canContinue =
    (step === "datetime" && !!date && !!slot) ||
    (step === "insurance" && !!paymentMethod) ||
    (step === "reason" && !!reason && (reason !== COMMON_REASONS[COMMON_REASONS.length - 1] || reasonDetail.trim().length > 2));

  const goNext = () => {
    if (step === "datetime") setStep("insurance");
    else if (step === "insurance") setStep("reason");
    else if (step === "reason") setStep("confirm");
  };
  const goBack = () => {
    if (step === "insurance") setStep("datetime");
    else if (step === "reason") setStep("insurance");
    else if (step === "confirm") setStep("reason");
  };

  const buildNotes = () => {
    const payLabel =
      paymentMethod === "self_pay" ? "Self-pay"
      : paymentMethod === "other" ? "Other insurance"
      : findInsurance(paymentMethod)?.short ?? paymentMethod;
    const reasonLabel = reason === COMMON_REASONS[COMMON_REASONS.length - 1]
      ? `Other: ${reasonDetail.trim()}`
      : reasonDetail.trim()
        ? `${reason} — ${reasonDetail.trim()}`
        : reason;
    return `Payment: ${payLabel}\nReason: ${reasonLabel}`;
  };

  const persistAndConfirm = async () => {
    if (!user?.email || !date || !slot) return;
    setSubmitting(true);
    try {
      const [hh, mm] = slot.split(":").map(Number);
      const apptAt = new Date(date);
      apptAt.setHours(hh, mm, 0, 0);
      let inserted: { id: string } | null = null;
      try {
        inserted = await createBookingFn({
          data: {
            listingId: providerId,
            serviceRequested: reason === COMMON_REASONS[COMMON_REASONS.length - 1]
              ? (reasonDetail.trim().slice(0, 80) || "Other")
              : reason,
            appointmentAtISO: apptAt.toISOString(),
            notes: buildNotes(),
          },
        });
      } catch (e: any) {
        toast.error("Could not save your appointment", { description: e?.message });
        return;
      }

      if (!inserted) {
        toast.error("Could not save your appointment");
        return;
      }
      trackListingMetric(providerId, "booking_clicks");
      try {
        const { notifyProviderMasked } = await import("@/lib/booking-notification.functions");
        void notifyProviderMasked({ data: { appointmentId: inserted.id } }).catch(() => {});
      } catch { /* ignore */ }
      toast.success(`Appointment booked — ${dateLabel} at ${format12h(slot)}`, {
        description: `With ${providerName}. Confirmation email on its way.`,
      });
      onOpenChange(false);
    } finally {
      setSubmitting(false);
    }
  };

  const handleConfirm = async () => {
    if (!date || !slot) return;
    if (!user) {
      if (typeof window !== "undefined") {
        const intent = {
          providerId, providerName,
          date: date.toISOString().slice(0, 10),
          slot,
          service: reason === COMMON_REASONS[COMMON_REASONS.length - 1]
            ? (reasonDetail.trim().slice(0, 80) || "Other")
            : reason,
          notes: buildNotes(),
        };
        localStorage.setItem("heallexa_pending_booking", JSON.stringify(intent));
        localStorage.setItem("heallexa_post_login_redirect", `/providers/${providerId}?book=1`);
        localStorage.setItem("selected_login_role", "patient");
        localStorage.setItem("heallexa_signup_account_type", "patient");
      }
      toast.message("Sign in to confirm your booking", {
        description: `${dateLabel} at ${format12h(slot)}`,
      });
      onOpenChange(false);
      navigate({ to: "/login/patient" });
      return;
    }
    await persistAndConfirm();
  };

  const steps: Array<{ key: Step; label: string; icon: any }> = [
    { key: "datetime", label: "Date & time", icon: CalendarIcon },
    { key: "insurance", label: "Coverage", icon: ShieldCheck },
    { key: "reason", label: "Reason", icon: FileText },
    { key: "confirm", label: "Confirm", icon: CheckCircle2 },
  ];
  const activeIdx = steps.findIndex((s) => s.key === step);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl p-0 overflow-hidden gap-0 bg-card">
        {/* Header */}
        <DialogHeader className="px-6 pt-6 pb-4 border-b border-border bg-gradient-to-br from-primary/5 via-background to-background">
          <div className="flex items-center gap-2 text-[11px] uppercase tracking-[0.18em] text-primary font-semibold">
            <Sparkles className="h-3.5 w-3.5" />
            Book an appointment
          </div>
          <DialogTitle className="text-2xl font-semibold tracking-tight">
            {providerName}
          </DialogTitle>
          <DialogDescription className="text-sm text-muted-foreground">
            A few quick steps and you're set.
          </DialogDescription>

          {/* Stepper */}
          <div className="mt-4 flex items-center gap-2">
            {steps.map((s, i) => {
              const Icon = s.icon;
              const done = i < activeIdx;
              const active = i === activeIdx;
              return (
                <div key={s.key} className="flex items-center flex-1 min-w-0">
                  <div
                    className={cn(
                      "flex items-center gap-2 rounded-full px-3 py-1.5 text-xs font-medium transition-colors",
                      active && "bg-primary text-primary-foreground shadow-sm",
                      done && "bg-primary/15 text-primary",
                      !active && !done && "bg-muted text-muted-foreground"
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    <span className="hidden sm:inline">{s.label}</span>
                    <span className="sm:hidden">{i + 1}</span>
                  </div>
                  {i < steps.length - 1 && (
                    <div className={cn("flex-1 h-px mx-2", i < activeIdx ? "bg-primary/40" : "bg-border")} />
                  )}
                </div>
              );
            })}
          </div>
        </DialogHeader>

        {/* Body */}
        <div className="px-6 py-5 max-h-[60vh] overflow-y-auto">
          {step === "datetime" && (
            <div className="grid grid-cols-1 md:grid-cols-[1fr_220px] gap-6">
              <div>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="text-base font-semibold">{monthLabel}</h3>
                  <div className="flex gap-1">
                    <Button
                      type="button" variant="outline" size="icon" className="h-8 w-8"
                      onClick={() => setMonth((m) => { const n = new Date(m); n.setMonth(n.getMonth() - 1); return n; })}
                    ><ChevronLeft className="h-4 w-4" /></Button>
                    <Button
                      type="button" variant="outline" size="icon" className="h-8 w-8"
                      onClick={() => setMonth((m) => { const n = new Date(m); n.setMonth(n.getMonth() + 1); return n; })}
                    ><ChevronRight className="h-4 w-4" /></Button>
                  </div>
                </div>
                <Calendar
                  mode="single"
                  selected={date}
                  onSelect={(d) => { setDate(d ?? undefined); setSlot(null); }}
                  month={month}
                  onMonthChange={setMonth}
                  disabled={isDayDisabled}
                  showOutsideDays
                  className="p-0 pointer-events-auto w-full [&_table]:w-full"
                />
              </div>
              <div className="border-l border-border md:pl-5 md:max-h-[360px] md:overflow-y-auto">
                <div className="flex items-center gap-2 text-sm font-semibold mb-3">
                  <Clock className="h-4 w-4 text-primary" />
                  {date ? dateLabel : "Pick a day"}
                </div>
                {!date && (
                  <p className="text-xs text-muted-foreground">
                    Select an available date to see open times.
                  </p>
                )}
                {date && daySlots.length === 0 && (
                  <p className="text-xs text-muted-foreground">No openings this day.</p>
                )}
                <div className="grid grid-cols-2 md:grid-cols-1 gap-2">
                  {daySlots.map((s) => {
                    const active = slot === s;
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setSlot(s)}
                        className={cn(
                          "rounded-md border text-sm py-2 px-3 font-medium transition-all",
                          active
                            ? "bg-primary text-primary-foreground border-primary shadow-sm scale-[1.02]"
                            : "border-primary/30 text-primary hover:bg-primary/5 hover:border-primary/60"
                        )}
                      >
                        {format12h(s)}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {step === "insurance" && (
            <div className="space-y-4 max-w-md mx-auto">
              <div>
                <h3 className="text-base font-semibold">How will you pay?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Select your insurance or choose self-pay.
                </p>
              </div>
              <Select value={paymentMethod} onValueChange={setPaymentMethod}>
                <SelectTrigger className="w-full h-11">
                  <SelectValue placeholder="Select coverage" />
                </SelectTrigger>
                <SelectContent className="max-h-[320px]">
                  <SelectItem value="self_pay">Self-pay (no insurance)</SelectItem>
                  {insuranceOptions.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground">
                        Accepted by this provider
                      </div>
                      {insuranceOptions.map((ins) => (
                        <SelectItem key={ins.id} value={ins.id}>
                          {ins.short}{ins.name ? ` — ${ins.name}` : ""}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  {otherInsuranceOptions.length > 0 && (
                    <>
                      <div className="px-2 py-1.5 mt-1 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground border-t border-border">
                        Other plans (call to verify)
                      </div>
                      {otherInsuranceOptions.map((ins) => (
                        <SelectItem key={ins.id} value={ins.id}>
                          {ins.short}{ins.name ? ` — ${ins.name}` : ""}
                        </SelectItem>
                      ))}
                    </>
                  )}
                  <SelectItem value="other">Other / not listed</SelectItem>
                </SelectContent>
              </Select>
              {paymentMethod && paymentMethod !== "self_pay" && paymentMethod !== "other" && (
                <p className="text-xs text-muted-foreground">
                  Bring your insurance card to the visit so the office can verify benefits.
                </p>
              )}
              {paymentMethod === "other" && (
                <p className="text-xs text-muted-foreground">
                  Your plan isn't listed — the office will reach out to verify coverage before your visit.
                </p>
              )}
            </div>
          )}

          {step === "reason" && (
            <div className="space-y-4 max-w-md mx-auto">
              <div>
                <h3 className="text-base font-semibold">What's the reason for your visit?</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Helps the provider prepare. Stay general — no medical details needed.
                </p>
              </div>
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger className="w-full h-11">
                  <SelectValue placeholder="Choose a reason" />
                </SelectTrigger>
                <SelectContent>
                  {COMMON_REASONS.map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <div>
                <label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                  {reason === COMMON_REASONS[COMMON_REASONS.length - 1] ? "Describe your reason" : "Anything to add? (optional)"}
                </label>
                <Textarea
                  value={reasonDetail}
                  onChange={(e) => setReasonDetail(e.target.value.slice(0, 280))}
                  placeholder="e.g. New patient, lower back has been bothering me for two weeks"
                  className="mt-1.5 min-h-[90px]"
                />
                <div className="text-[11px] text-muted-foreground text-right mt-1">{reasonDetail.length}/280</div>
              </div>
            </div>
          )}

          {step === "confirm" && (
            <div className="space-y-5 max-w-md mx-auto">
              <div className="text-center">
                <div className="mx-auto h-12 w-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                  <CheckCircle2 className="h-6 w-6 text-primary" />
                </div>
                <h3 className="text-lg font-semibold">Review & confirm</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  {user ? "Confirm to book this appointment." : "Sign in to complete your booking."}
                </p>
              </div>
              <div className="border border-border rounded-lg divide-y divide-border bg-background">
                <SummaryRow label="Provider" value={providerName} />
                <SummaryRow label="When" value={date && slot ? `${dateLabel} · ${format12h(slot)}` : "—"} />
                <SummaryRow label="Coverage" value={
                  paymentMethod === "self_pay" ? "Self-pay"
                  : paymentMethod === "other" ? "Other insurance"
                  : findInsurance(paymentMethod)?.short ?? "—"
                } />
                <SummaryRow label="Reason" value={
                  reason === COMMON_REASONS[COMMON_REASONS.length - 1]
                    ? (reasonDetail.trim() || "Other")
                    : reason
                } />
              </div>
              {!user && !authLoading && (
                <div className="border border-primary/30 bg-primary/5 rounded-lg p-3 text-sm flex items-start gap-2">
                  <LogIn className="h-4 w-4 text-primary mt-0.5 shrink-0" />
                  <div>
                    We'll save your selection and bring you right back here after you sign in or create your free account.
                  </div>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer */}
        <DialogFooter className="px-6 py-4 border-t border-border bg-muted/30 flex !flex-row !justify-between gap-2">
          <Button
            type="button"
            variant="ghost"
            onClick={step === "datetime" ? () => onOpenChange(false) : goBack}
            disabled={submitting}
          >
            {step === "datetime" ? "Cancel" : (<><ArrowLeft className="h-4 w-4 mr-1.5" /> Back</>)}
          </Button>
          {step !== "confirm" ? (
            <Button type="button" onClick={goNext} disabled={!canContinue}>
              Continue <ArrowRight className="h-4 w-4 ml-1.5" />
            </Button>
          ) : (
            <Button type="button" onClick={handleConfirm} disabled={submitting || authLoading}>
              {submitting ? <><Loader2 className="h-4 w-4 mr-1.5 animate-spin" /> Booking…</>
                : user ? <><CheckCircle2 className="h-4 w-4 mr-1.5" /> Confirm booking</>
                : <><LogIn className="h-4 w-4 mr-1.5" /> Sign in to confirm</>}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SummaryRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-center justify-between px-4 py-3 text-sm">
      <span className="text-muted-foreground">{label}</span>
      <span className="font-medium text-foreground text-right max-w-[60%] truncate">{value}</span>
    </div>
  );
}
